import { DOM } from './state.js';
import {
    StaffGiftcardsShell,
    EmitirForm,
    GiftcardResultado,
    GiftcardAcciones,
    CanjearShell,
    CanjearLista,
    GiftcardsAviso,
} from './components/giftcards/StaffGiftcardsView.js';
import { AdminGiftcardsView } from './components/giftcards/AdminGiftcardsView.js';
import { renderGiftcard } from './components/giftcards/GiftcardImage.js';
import { downloadBlob } from './infrastructure/browser-download.js';
import {
    MONTOS_RAPIDOS,
    MEDIOS,
    ESTADOS,
    emitir,
    cargarVigentes,
    filtrarPorPara,
    canjear,
    cargarGiftcards,
    anular,
    etiquetaMedio,
    modeloImagen,
} from './services/giftcards.service.js';

/**
 * Gift cards (openspec/changes/staff-giftcards/spec.md): staff "Gift cards"
 * (`giftcards`, issue and redeem) and admin "Gift cards" (`giftcards-admin`,
 * states, totals, void). Registered in `SECTION_REGISTRY` and
 * `renderActiveTab` (js/admin.js), same shape as js/reportes.js: each
 * `mountGiftcards*Section(mount)` owns its state and listeners, and every
 * async result checks its section's `seq` (bumped by mount and teardown).
 */

// ---------------------------------------------------------------------------
// Card image panel ("visor"): shared by staff (after issuing) and admin
// (redraw any card). Its buttons stay disabled until the PNG exists; a failed
// drawing says so and can be retried, so a paid card never ends up with no
// way to get its image (review reliability-1 / resilience-3).
// ---------------------------------------------------------------------------

/**
 * @typedef {{
 *   imagen: { para: string, deParte: string | null, mensaje: string | null, monto: number, validaHasta: string },
 *   blob: Blob | null,
 *   estado: 'dibujando'|'lista'|'error',
 *   aviso: { tipo: 'ok'|'error', texto: string } | null,
 *   token: number,
 *   cierre: 'nueva'|'cerrar',
 * }} Visor
 */

/** @returns {Visor} */
function nuevoVisor(imagen, cierre) {
    return { imagen, blob: null, estado: 'dibujando', aviso: null, token: 0, cierre };
}

/** @param {Visor} visor @param {HTMLElement} region */
function pintarAcciones(visor, region) {
    const slot = region.querySelector('[data-giftcard-acciones]');
    if (slot) {
        slot.innerHTML = GiftcardAcciones({
            estado: visor.estado,
            puedeCompartir: puedeCompartirArchivos(),
            puedeCopiar: puedeCopiarImagen(),
            cierre: visor.cierre,
            aviso: visor.aviso,
        });
    }
}

/** Paints the panel into `region` and draws the card on its canvas. @param {Visor} visor @param {HTMLElement} region @param {string} [titulo] */
function mostrarVisor(visor, region, titulo) {
    region.hidden = false;
    region.innerHTML = GiftcardResultado({ imagen: visor.imagen, titulo });
    void dibujar(visor, region);
}

/** @param {Visor} visor @param {HTMLElement} region */
async function dibujar(visor, region) {
    const token = ++visor.token;
    visor.estado = 'dibujando';
    visor.blob = null;
    visor.aviso = null;
    pintarAcciones(visor, region);
    const canvas = region.querySelector('canvas');
    let blob = null;
    try {
        blob = canvas ? await renderGiftcard(canvas, visor.imagen) : null;
        if (!blob) throw new Error('renderGiftcard devolvió null');
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('giftcards: no se pudo dibujar la imagen', err);
    }
    if (token !== visor.token || !region.isConnected) return;
    visor.blob = blob;
    visor.estado = blob ? 'lista' : 'error';
    pintarAcciones(visor, region);
}

function puedeCompartirArchivos() {
    try {
        return typeof navigator.canShare === 'function' && navigator.canShare({ files: [new File([''], 'gift-card.png', { type: 'image/png' })] });
    } catch {
        return false;
    }
}

/** `gift-card-panache-lucia-perez.png` */
function nombreArchivo(para) {
    const slug = String(para)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return `gift-card-panache${slug ? `-${slug}` : ''}.png`;
}

/** @param {Visor | null} visor */
function descargar(visor) {
    if (!visor?.blob) return;
    downloadBlob({ filename: nombreArchivo(visor.imagen.para), blob: visor.blob });
}

/** @param {Visor | null} visor @param {HTMLElement | null} region */
async function compartir(visor, region) {
    if (!visor?.blob) return;
    const token = visor.token;
    const file = new File([visor.blob], nombreArchivo(visor.imagen.para), { type: 'image/png' });
    try {
        await navigator.share({ files: [file], title: 'Gift card Panache' });
    } catch (err) {
        if (/** @type {Error} */ (err)?.name === 'AbortError') return;
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('giftcards: no se pudo compartir', err);
        if (token !== visor.token) return;
        visor.aviso = { tipo: 'error', texto: 'No se pudo compartir. Descargá la imagen y mandala por WhatsApp.' };
        if (region?.isConnected) pintarAcciones(visor, region);
    }
}

function puedeCopiarImagen() {
    return typeof window.ClipboardItem === 'function' && typeof navigator.clipboard?.write === 'function';
}

/**
 * The PNG to the clipboard as an image, so the counter pastes it straight
 * into WhatsApp (Ctrl+V). It starts inside the click (a user gesture).
 * @param {Visor | null} visor @param {HTMLElement | null} region
 */
async function copiar(visor, region) {
    if (!visor?.blob) return;
    const token = visor.token;
    let aviso;
    try {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': visor.blob })]);
        aviso = { tipo: 'ok', texto: 'Imagen copiada. Pegala en WhatsApp con Ctrl+V.' };
    } catch (err) {
        window.Sentry?.captureException(err);
        // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
        console.error('giftcards: no se pudo copiar la imagen', err);
        aviso = { tipo: 'error', texto: 'No se pudo copiar. Descargá la imagen y adjuntala en WhatsApp.' };
    }
    if (token !== visor.token) return;
    visor.aviso = aviso;
    if (region?.isConnected) pintarAcciones(visor, region);
}

// Print: only the image, through a body class and a print-only node
// (styles.src.css, same approach as the comprobante de liquidación).
let impresion = /** @type {{ url: string, nodo: HTMLElement } | null} */ (null);

/** @param {Visor | null} visor @param {HTMLElement | null} region */
function imprimir(visor, region) {
    if (!visor?.blob) return;
    const token = visor.token;
    terminarImpresion();
    const url = URL.createObjectURL(visor.blob);
    const nodo = document.createElement('div');
    nodo.id = 'giftcard-print';
    nodo.innerHTML = `<img src="${url}" alt="Gift card">`;
    document.body.appendChild(nodo);
    document.body.classList.add('giftcard-imprimiendo');
    impresion = { url, nodo };
    window.addEventListener('afterprint', terminarImpresion, { once: true });
    const img = nodo.querySelector('img');
    const lanzar = () => window.print();
    if (img?.complete) {
        lanzar();
    } else {
        img?.addEventListener('load', lanzar, { once: true });
        img?.addEventListener(
            'error',
            () => {
                terminarImpresion();
                const err = new Error('giftcards: no cargó la imagen para imprimir');
                window.Sentry?.captureException(err);
                // biome-ignore lint/suspicious/noConsole: observability surface (no hay Sentry en browser).
                console.error(err.message);
                if (token !== visor.token) return;
                visor.aviso = { tipo: 'error', texto: 'No se pudo preparar la impresión. Descargá la imagen e imprimila desde ahí.' };
                if (region?.isConnected) pintarAcciones(visor, region);
            },
            { once: true },
        );
    }
}

/**
 * Routes the image panel's own actions; the staff and admin handlers both
 * delegate here. @returns {boolean} whether `action` was a panel action
 * @param {string | undefined} action @param {Visor | null} visor @param {HTMLElement | null} region
 */
function accionVisor(action, visor, region) {
    if (action === 'giftcard-descargar') descargar(visor);
    else if (action === 'giftcard-copiar') void copiar(visor, region);
    else if (action === 'giftcard-compartir') void compartir(visor, region);
    else if (action === 'giftcard-imprimir') imprimir(visor, region);
    else if (action === 'giftcard-imagen-reintentar') {
        if (visor && region) void dibujar(visor, region);
    } else return false;
    return true;
}

function terminarImpresion() {
    if (!impresion) return;
    document.body.classList.remove('giftcard-imprimiendo');
    impresion.nodo.remove();
    URL.revokeObjectURL(impresion.url);
    impresion = null;
}

// ---------------------------------------------------------------------------
// Staff: emitir y canjear
// ---------------------------------------------------------------------------

const staff = {
    seq: 0,
    loadSeq: 0,
    modo: /** @type {'emitir'|'canjear'} */ ('emitir'),
    emitiendo: false,
    /** The last issued card stays on screen (and comes back with the Emitir mode) until "Emitir otra". */
    visor: /** @type {Visor | null} */ (null),
    uiState: /** @type {{ status: 'loading'|'error'|'ready', mensaje?: string }} */ ({ status: 'loading' }),
    vigentes: /** @type {Array<Record<string, any>>} */ ([]),
    filtro: '',
    confirmando: /** @type {string | null} */ (null),
    canjeando: /** @type {string | null} */ (null),
};

const staffRoot = () => DOM.adminContainer?.querySelector('[data-giftcards-staff]') ?? null;
const staffContenido = () => DOM.adminContainer?.querySelector('#giftcards-staff-contenido') ?? null;
const staffVisorRegion = () => staffContenido()?.querySelector('[data-giftcard-resultado]') ?? null;

/**
 * Entry point of the staff "Gift cards" tab (`SECTION_REGISTRY.giftcards.mount`).
 * @param {HTMLElement | null} mount
 */
export function mountGiftcardsStaffSection(mount) {
    if (!mount) return;
    stopGiftcardsStaff();
    staff.modo = 'emitir';
    staff.emitiendo = false;
    staff.visor = null;
    staff.filtro = '';
    staff.confirmando = null;
    staff.canjeando = null;

    mount.innerHTML = StaffGiftcardsShell({ modo: staff.modo });
    const root = staffRoot();
    root?.addEventListener('click', handleStaffClick);
    root?.addEventListener('submit', handleStaffSubmit);
    root?.addEventListener('input', handleStaffInput);
    pintarModo();
}

/** Teardown when leaving the tab or logging out: in-flight responses become stale. */
export function stopGiftcardsStaff() {
    staff.seq += 1;
    if (staff.visor) staff.visor.token += 1;
    terminarImpresion();
}

function pintarModo() {
    const root = staffRoot();
    const contenido = staffContenido();
    if (!root || !contenido) return;
    for (const b of root.querySelectorAll('[data-action="giftcards-modo"]')) {
        const activo = b.dataset.modo === staff.modo;
        b.setAttribute('aria-pressed', String(activo));
        for (const c of ['bg-brand', 'text-white', 'shadow-lg']) b.classList.toggle(c, activo);
        for (const c of ['bg-surface', 'border', 'border-border-subtle', 'text-text-muted']) b.classList.toggle(c, !activo);
    }
    if (staff.modo === 'emitir') {
        contenido.innerHTML = EmitirForm({ montosRapidos: MONTOS_RAPIDOS, medios: MEDIOS });
        const region = staffVisorRegion();
        if (staff.visor && region) mostrarVisor(staff.visor, region);
    } else {
        staff.filtro = '';
        staff.confirmando = null;
        contenido.innerHTML = CanjearShell();
        void cargarLista();
    }
}

/** The mode buttons are disabled while an issue or a redeem is in flight. */
function pintarOcupado() {
    const ocupado = staff.emitiendo || Boolean(staff.canjeando);
    for (const b of staffRoot()?.querySelectorAll('[data-action="giftcards-modo"]') ?? []) b.disabled = ocupado;
}

/** @param {{ tipo: 'ok'|'error', texto: string } | null} aviso */
function pintarAviso(aviso) {
    const region = staffContenido()?.querySelector('[data-giftcards-aviso]');
    if (region) region.innerHTML = GiftcardsAviso(aviso);
}

/** @param {MouseEvent} e */
function handleStaffClick(e) {
    const boton = /** @type {HTMLElement} */ (e.target).closest('[data-action]');
    if (!boton) return;
    const { action, id } = boton.dataset;
    if (action === 'giftcards-modo') {
        // Never leave a mode while its request is in flight: the answer would
        // land on the other mode's screen (review reliability-1, -4).
        if (staff.emitiendo || staff.canjeando) return;
        const modo = boton.dataset.modo === 'canjear' ? 'canjear' : 'emitir';
        if (modo === staff.modo) return;
        staff.modo = modo;
        pintarModo();
    } else if (action === 'giftcard-monto-rapido') {
        const input = staffContenido()?.querySelector('input[name="monto"]');
        if (input) input.value = boton.dataset.monto ?? '';
    } else if (accionVisor(action, staff.visor, staffVisorRegion())) {
        // handled by the image panel
    } else if (action === 'giftcard-nueva') {
        staff.visor = null;
        pintarModo();
    } else if (action === 'giftcard-canjear') {
        staff.confirmando = id ?? null;
        pintarLista();
    } else if (action === 'giftcard-canjear-cancelar') {
        staff.confirmando = null;
        pintarLista();
    } else if (action === 'giftcard-canjear-confirmar') {
        void canjearGiftcard(id);
    } else if (action === 'giftcards-reintentar') {
        void cargarLista();
    }
}

/** @param {InputEvent} e */
function handleStaffInput(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (!target.matches('[data-giftcards-buscar]')) return;
    staff.filtro = /** @type {HTMLInputElement} */ (target).value;
    staff.confirmando = null;
    pintarLista();
}

/** @param {SubmitEvent} e */
async function handleStaffSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form.matches('form[data-action-form="giftcard-emitir"]')) return;
    e.preventDefault();
    if (staff.emitiendo) return;
    const datos = {
        monto: form.querySelector('input[name="monto"]')?.value ?? '',
        medio: form.querySelector('input[name="medio"]:checked')?.value ?? '',
        para: form.querySelector('input[name="para"]')?.value ?? '',
        deParte: form.querySelector('input[name="de_parte"]')?.value ?? '',
        mensaje: form.querySelector('textarea[name="mensaje"]')?.value ?? '',
    };

    const seq = staff.seq;
    staff.emitiendo = true;
    pintarOcupado();
    const boton = form.querySelector('[data-giftcards-emitir-boton]');
    if (boton) boton.disabled = true;
    pintarAviso(null);

    const res = await emitir(datos);
    if (seq !== staff.seq) return;
    staff.emitiendo = false;
    pintarOcupado();
    if (boton) boton.disabled = false;
    if (!res.ok) {
        pintarAviso({ tipo: 'error', texto: res.mensaje });
        return;
    }

    form.reset();
    // The previous card's drawing may still be running: make it stale so it
    // never paints over this one (re-review reliability-1).
    if (staff.visor) staff.visor.token += 1;
    staff.visor = nuevoVisor(res.imagen, 'nueva');
    const region = staffVisorRegion();
    if (!region) return;
    mostrarVisor(staff.visor, region);
    region.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

async function cargarLista() {
    const seq = staff.seq;
    const loadSeq = ++staff.loadSeq;
    staff.uiState = { status: 'loading' };
    pintarLista();
    const res = await cargarVigentes();
    if (seq !== staff.seq || loadSeq !== staff.loadSeq) return;
    if (res.ok) {
        staff.vigentes = res.items;
        staff.uiState = { status: 'ready' };
    } else {
        staff.vigentes = [];
        staff.uiState = { status: 'error', mensaje: res.mensaje };
    }
    pintarLista();
}

function pintarLista() {
    const region = staffContenido()?.querySelector('[data-giftcards-lista]');
    if (!region) return;
    region.innerHTML = CanjearLista({
        uiState: staff.uiState,
        items: filtrarPorPara(staff.vigentes, staff.filtro),
        hayFiltro: staff.filtro.trim() !== '',
        confirmando: staff.confirmando,
        canjeando: staff.canjeando,
    });
}

/** @param {string | undefined} id */
async function canjearGiftcard(id) {
    if (!id || staff.canjeando) return;
    const seq = staff.seq;
    staff.canjeando = id;
    pintarOcupado();
    pintarLista();
    const res = await canjear(id);
    if (seq !== staff.seq) return;
    staff.canjeando = null;
    pintarOcupado();
    staff.confirmando = null;
    pintarAviso(res.ok ? { tipo: 'ok', texto: 'Gift card canjeada.' } : { tipo: 'error', texto: res.mensaje });
    // A refusal (someone redeemed it already, it expired) also means the list is out of date.
    await cargarLista();
}

// ---------------------------------------------------------------------------
// Admin: estados, totales, anular
// ---------------------------------------------------------------------------

const admin = {
    seq: 0,
    loadSeq: 0,
    estado: /** @type {'vigentes'|'canjeadas'|'vencidas'|'anuladas'} */ ('vigentes'),
    uiState: /** @type {{ status: 'loading'|'error'|'ready', mensaje?: string }} */ ({ status: 'loading' }),
    items: /** @type {Array<Record<string, any>>} */ ([]),
    totales: /** @type {Record<string, { cantidad: number, monto: number }>} */ ({}),
    anulando: /** @type {string | null} */ (null),
    enviando: false,
    motivo: '',
    visor: /** @type {Visor | null} */ (null),
    aviso: /** @type {{ tipo: 'ok'|'error', texto: string } | null} */ (null),
    avisoAnular: /** @type {{ tipo: 'ok'|'error', texto: string } | null} */ (null),
};

const ESTADOS_VALIDOS = new Set(ESTADOS.map((e) => e.value));
const adminRoot = () => DOM.adminContainer?.querySelector('#giftcards-admin-mount') ?? null;
const adminVisorRegion = () => DOM.adminContainer?.querySelector('#giftcards-admin-visor') ?? null;

/**
 * Entry point of the admin "Gift cards" tab (`SECTION_REGISTRY['giftcards-admin'].mount`).
 * @param {HTMLElement | null} mount
 */
export function mountGiftcardsAdminSection(mount) {
    if (!mount) return;
    stopGiftcardsAdmin();
    admin.estado = 'vigentes';
    admin.uiState = { status: 'loading' };
    admin.items = [];
    admin.totales = {};
    admin.anulando = null;
    admin.enviando = false;
    admin.motivo = '';
    admin.aviso = null;
    admin.avisoAnular = null;
    admin.visor = null;

    // The image panel lives outside the list root, so list repaints never
    // wipe a canvas being drawn.
    mount.innerHTML = '<div data-giftcards-admin-wrap><div id="giftcards-admin-visor" class="mb-6" hidden></div><div id="giftcards-admin-mount"></div></div>';
    const wrap = mount.querySelector('[data-giftcards-admin-wrap]');
    wrap?.addEventListener('click', handleAdminClick);
    wrap?.addEventListener('submit', handleAdminSubmit);
    wrap?.addEventListener('input', handleAdminInput);
    void cargarAdmin();
}

/** Teardown when leaving the tab or logging out. */
export function stopGiftcardsAdmin() {
    admin.seq += 1;
    if (admin.visor) admin.visor.token += 1;
    terminarImpresion();
}

function renderAdminView() {
    const root = adminRoot();
    if (!root) return;
    root.innerHTML = AdminGiftcardsView({
        uiState: admin.uiState,
        estado: admin.estado,
        estados: ESTADOS,
        items: admin.items,
        totales: admin.totales,
        anulando: admin.anulando,
        enviando: admin.enviando,
        motivo: admin.motivo,
        etiquetaMedio,
    });
    const aviso = root.querySelector('[data-giftcards-aviso]');
    if (aviso) aviso.innerHTML = GiftcardsAviso(admin.aviso);
    const avisoAnular = root.querySelector('[data-giftcards-aviso-anular]');
    if (avisoAnular) avisoAnular.innerHTML = GiftcardsAviso(admin.avisoAnular);
}

async function cargarAdmin() {
    const seq = admin.seq;
    const loadSeq = ++admin.loadSeq;
    admin.uiState = { status: 'loading' };
    renderAdminView();
    const res = await cargarGiftcards(admin.estado);
    if (seq !== admin.seq || loadSeq !== admin.loadSeq) return;
    if (res.ok) {
        admin.items = res.items;
        admin.totales = res.totales;
        admin.uiState = { status: 'ready' };
    } else {
        admin.items = [];
        admin.uiState = { status: 'error', mensaje: res.mensaje };
    }
    renderAdminView();
}

/** @param {MouseEvent} e */
function handleAdminClick(e) {
    const boton = /** @type {HTMLElement} */ (e.target).closest('[data-action]');
    if (!boton) return;
    const { action, id } = boton.dataset;
    if (action === 'giftcards-filtro') {
        const estado = boton.dataset.estado;
        if (!ESTADOS_VALIDOS.has(estado) || estado === admin.estado) return;
        admin.estado = /** @type {typeof admin.estado} */ (estado);
        admin.anulando = null;
        admin.aviso = null;
        void cargarAdmin();
    } else if (action === 'giftcards-reintentar') {
        void cargarAdmin();
    } else if (action === 'giftcard-anular') {
        if (admin.enviando) return;
        admin.anulando = id ?? null;
        admin.motivo = '';
        admin.avisoAnular = null;
        renderAdminView();
        adminRoot()?.querySelector('textarea[name="motivo"]')?.focus();
    } else if (action === 'giftcard-anular-cancelar') {
        admin.anulando = null;
        admin.avisoAnular = null;
        renderAdminView();
    } else if (action === 'giftcard-imagen') {
        const card = admin.items.find((g) => g.id === id);
        const region = adminVisorRegion();
        if (!card || !region) return;
        if (admin.visor) admin.visor.token += 1;
        admin.visor = nuevoVisor(modeloImagen(card), 'cerrar');
        mostrarVisor(admin.visor, region, 'Imagen de la gift card');
        region.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    } else if (action === 'giftcard-visor-cerrar') {
        if (admin.visor) admin.visor.token += 1;
        admin.visor = null;
        const region = adminVisorRegion();
        if (region) {
            region.hidden = true;
            region.innerHTML = '';
        }
    } else {
        accionVisor(action, admin.visor, adminVisorRegion());
    }
}

/** @param {InputEvent} e */
function handleAdminInput(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.matches('textarea[name="motivo"]')) admin.motivo = /** @type {HTMLTextAreaElement} */ (target).value;
}

/** @param {SubmitEvent} e */
async function handleAdminSubmit(e) {
    const form = /** @type {HTMLFormElement} */ (e.target);
    if (!form.matches('form[data-action-form="giftcard-anular"]')) return;
    e.preventDefault();
    const id = form.dataset.id;
    if (!id || admin.enviando) return;
    admin.motivo = form.querySelector('textarea[name="motivo"]')?.value ?? admin.motivo;

    // The form stays the same node while it is open (no repaint): a refusal
    // is painted inside it and the typed motivo is never lost.
    const pintarEnForm = (aviso) => {
        admin.avisoAnular = aviso;
        const region = form.querySelector('[data-giftcards-aviso-anular]');
        if (region) region.innerHTML = GiftcardsAviso(aviso);
    };
    const botones = [...form.querySelectorAll('button')];
    const seq = admin.seq;
    admin.enviando = true;
    for (const b of botones) b.disabled = true;
    pintarEnForm(null);
    const res = await anular({ id, motivo: admin.motivo });
    if (seq !== admin.seq) return;
    admin.enviando = false;
    for (const b of botones) b.disabled = false;
    if (!res.ok) {
        pintarEnForm({ tipo: 'error', texto: res.mensaje });
        return;
    }
    admin.anulando = null;
    admin.motivo = '';
    admin.aviso = { tipo: 'ok', texto: 'Gift card anulada.' };
    await cargarAdmin();
}
